var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useImmer } from 'use-immer';
import { v4 as uuidv4 } from 'uuid';
import { FaPlus, FaTrash, FaTimes, FaPencilAlt, FaSearch } from 'react-icons/fa';
import { getItemImages } from '../../api/item-images';
import { getLevelItems, createItem, deleteItem, updateItem } from '../../api/items';
import './ItemsManager.scss';
const ItemsManager = ({ levelId }) => {
    const [items, updateItems] = useImmer([]);
    const [images, setImages] = useState([]);
    const [currentItem, setCurrentItem] = useImmer(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const filteredItems = items.filter(item => searchTerm ? item.name.toLowerCase().includes(searchTerm.toLowerCase()) : true);
    const load = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        setError(null);
        try {
            const [it, imgs] = yield Promise.all([
                getLevelItems(levelId),
                getItemImages(levelId)
            ]);
            updateItems(() => it);
            setImages(imgs);
        }
        catch (err) {
            console.error('Error fetching items:', err);
            setError(`Failed to load items: ${err.message}`);
        }
        finally {
            setIsLoading(false);
        }
    });
    useEffect(() => { load(); }, [levelId]);
    const handleCreateItem = () => {
        const newItem = {
            id: uuidv4(),
            levelId,
            name: '',
            imageId: null,
            isArchived: false,
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        setCurrentItem(newItem);
        setIsEditing(true);
        setSelectedItemId(null);
        setError(null);
    };
    const handleEditItem = (itemId) => {
        const itemToEdit = items.find(i => i.id === itemId);
        if (itemToEdit) {
            setCurrentItem(itemToEdit);
            setIsEditing(true);
            setSelectedItemId(itemId);
            setError(null);
        }
    };
    const handleDeleteItem = (itemId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!confirm('Are you sure you want to delete this item?'))
            return;
        setIsLoading(true);
        setError(null);
        try {
            yield deleteItem(levelId, itemId);
            updateItems(draft => draft.filter(i => i.id !== itemId));
            if (selectedItemId === itemId) {
                setSelectedItemId(null);
                setCurrentItem(null);
                setIsEditing(false);
            }
        }
        catch (err) {
            console.error('Error deleting item:', err);
            setError(`Failed to delete item: ${err.message}`);
        }
        finally {
            setIsLoading(false);
        }
    });
    const handleSaveItem = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!currentItem || !currentItem.name.trim()) {
            setError('Item name is required');
            return;
        }
        setIsSaving(true);
        setError(null);
        try {
            if (selectedItemId) {
                // Update existing
                const updated = yield updateItem(levelId, selectedItemId, currentItem);
                updateItems(draft => {
                    const index = draft.findIndex(i => i.id === selectedItemId);
                    if (index !== -1)
                        draft[index] = updated;
                });
            }
            else {
                // Create new
                const created = yield createItem(levelId, currentItem);
                updateItems(draft => { draft.unshift(created); });
            }
            setIsEditing(false);
            setCurrentItem(null);
            setSelectedItemId(null);
        }
        catch (err) {
            console.error('Error saving item:', err);
            setError(`Failed to save item: ${err.message}`);
        }
        finally {
            setIsSaving(false);
        }
    });
    const handleCancelEdit = () => {
        setIsEditing(false);
        setCurrentItem(null);
        setSelectedItemId(null);
        setError(null);
    };
    const handleItemFieldChange = (field, value) => {
        setCurrentItem(draft => {
            if (draft)
                draft[field] = value;
        });
    };
    const renderItemsList = () => (_jsxs("div", { className: "items-list", children: [_jsx("div", { className: "items-toolbar", children: _jsx("div", { className: "width-container", children: _jsx("div", { className: "toolbar-content", children: _jsxs("div", { className: "search-container", children: [_jsx(FaSearch, { className: "search-icon", size: 14 }), _jsx("input", { type: "text", placeholder: "Search by name...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), className: "search-input" }), searchTerm && (_jsx("button", { className: "clear-search-button", onClick: () => setSearchTerm(''), title: "Clear search", children: _jsx(FaTimes, { size: 12 }) }))] }) }) }) }), _jsxs("div", { className: "items-content", children: [_jsxs("div", { className: "items-header", children: [_jsx("h2", { className: "section-title", children: "Items" }), _jsxs("button", { className: "add-button", onClick: handleCreateItem, title: "Create new item", disabled: isLoading, children: [_jsx(FaPlus, {}), " Add Item"] })] }), error && _jsx("div", { className: "error-message", children: error }), isLoading ? (_jsx("div", { className: "loading-state", children: "Loading items..." })) : filteredItems.length === 0 ? (_jsx("div", { className: "empty-state", children: items.length === 0 ? (_jsxs(_Fragment, { children: [_jsx("p", { children: "No items yet. Create your first item!" }), _jsxs("button", { onClick: handleCreateItem, className: "primary-button", children: [_jsx(FaPlus, {}), " Create Item"] })] })) : (_jsx("p", { children: "No items match your search criteria." })) })) : (_jsx("div", { className: "items-grid", children: filteredItems.map(item => {
                            const img = images.find(i => i.id === item.imageId);
                            return (_jsxs("div", { className: `item-card ${selectedItemId === item.id ? 'selected' : ''}`, onClick: () => handleEditItem(item.id), style: {
                                    backgroundImage: img ? `url(${img.imageUrl})` : undefined,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }, children: [_jsx("div", { className: "item-card-overlay", children: _jsx("div", { className: "item-card-content", children: _jsx("h3", { className: "item-title", children: item.name }) }) }), _jsxs("div", { className: "item-card-actions", children: [_jsx("button", { className: "icon-button edit-button", onClick: (e) => {
                                                    e.stopPropagation();
                                                    handleEditItem(item.id);
                                                }, title: "Edit", children: _jsx(FaPencilAlt, { size: 12 }) }), _jsx("button", { className: "icon-button delete-button", onClick: (e) => {
                                                    e.stopPropagation();
                                                    handleDeleteItem(item.id);
                                                }, title: "Delete", children: _jsx(FaTrash, { size: 12 }) })] })] }, item.id));
                        }) }))] })] }));
    const renderItemEditor = () => {
        if (!currentItem)
            return null;
        return (_jsxs("div", { className: "item-editor", children: [_jsxs("div", { className: "editor-header", children: [_jsx("h2", { children: selectedItemId ? 'Edit Item' : 'Create New Item' }), _jsxs("div", { className: "editor-actions", children: [_jsxs("button", { className: "cancel-button", onClick: handleCancelEdit, disabled: isSaving, children: [_jsx(FaTimes, {}), " Cancel"] }), _jsx("button", { className: "save-button", onClick: handleSaveItem, disabled: isSaving, children: isSaving ? 'Saving...' : 'Save Item' })] })] }), error && _jsx("div", { className: "error-message", children: error }), _jsx("div", { className: "editor-form", children: _jsxs("div", { className: "form-section", children: [_jsx("h3", { className: "section-title", children: "Basic Information" }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "itemName", children: "Item Name:" }), _jsx("input", { type: "text", id: "itemName", value: currentItem.name, onChange: (e) => handleItemFieldChange('name', e.target.value), placeholder: "Enter item name", disabled: isSaving })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "itemImage", children: "Image:" }), _jsxs("select", { id: "itemImage", value: currentItem.imageId || '', onChange: (e) => handleItemFieldChange('imageId', e.target.value || null), disabled: isSaving, children: [_jsx("option", { value: "", children: "No image" }), images.map(img => (_jsx("option", { value: img.id, children: img.filename }, img.id)))] })] }), currentItem.imageId && (() => {
                                const img = images.find(i => i.id === currentItem.imageId);
                                return img ? (_jsx("div", { className: "image-preview", children: _jsx("img", { src: img.imageUrl, alt: img.filename }) })) : null;
                            })()] }) })] }));
    };
    return (_jsx("div", { className: "items-manager-container", children: isEditing ? renderItemEditor() : renderItemsList() }));
};
export default ItemsManager;
